<template>
  <el-container class="admin-pc-layout">
    <el-aside width="205px" class="admin-pc-aside">
      <div class="admin-pc-aside__logo">
        <span class="admin-pc-aside__brand">逻拓后管系统</span>
        <el-dropdown trigger="click" @command="handleCommand">
          <span class="admin-pc-aside__user">
            <i class="el-icon-user-solid" />
            {{ displayName }}
            <i class="el-icon-arrow-down el-icon--right" />
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div v-if="showSchoolsOrgBar" class="admin-pc-aside__org">
        <template v-if="isPlatformAdmin">
          <el-select
            v-model="activeOrgId"
            size="mini"
            filterable
            clearable
            placeholder="选择机构"
            class="admin-pc-aside__org-select"
            @change="handleOrgSwitch"
          >
            <el-option
              v-for="item in orgOptions"
              :key="item.id"
              :label="item.orgName"
              :value="item.id"
            />
          </el-select>
        </template>
        <template v-else>
          <span class="admin-pc-aside__org-label" :title="currentOrgLabel">
            <i class="el-icon-office-building" />
            {{ currentOrgLabel }}
          </span>
        </template>
      </div>
      <el-menu
        :key="'h5-admin-menu-' + sideMenuKey"
        :default-active="activeMenu"
        :default-openeds="defaultOpeneds"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
        router
      >
        <el-menu-item
          v-for="item in visibleH5Menus"
          :key="item.index"
          :index="item.index"
        >
          <i :class="item.icon" />
          <span slot="title">{{ item.title }}</span>
        </el-menu-item>
        <el-submenu v-if="visibleSchoolsMenus.length" index="schools">
          <template slot="title">
            <i class="el-icon-reading" />
            <span>教培管理</span>
          </template>
          <el-menu-item
            v-for="item in visibleSchoolsMenus"
            :key="item.index"
            :index="item.index"
          >
            <i :class="item.icon" />
            <span slot="title">{{ item.title }}</span>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </el-aside>

    <el-main class="admin-pc-main">
      <el-alert
        v-if="schoolsOrgMissingTip"
        class="admin-pc-org-tip"
        type="warning"
        :closable="false"
        show-icon
        :title="schoolsOrgMissingTip"
      />
      <div
        v-loading="routeLoading"
        element-loading-text="加载中..."
        class="admin-pc-main__view"
      >
        <transition name="admin-page-fade" mode="out-in">
          <router-view :key="$route.fullPath" />
        </transition>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { getH5AdminToken, getSchoolsActiveOrgId, setSchoolsActiveOrgId } from '@/utils/h5-admin-request'
import {
  getH5AdminUserCache,
  h5AdminGetInfo,
  h5AdminLogout,
  removeH5AdminSession
} from '@/api/h5-admin-auth'
import { fetchSchoolsOrgSession } from '@/api/h5-admin-schools'
import {
  filterSchoolsAdminMenus,
  resolveSchoolsAdminActiveMenu
} from '@/pages/schools/config/admin-menus'
import {
  filterH5AdminMenus,
  resolveH5AdminActiveMenu
} from '@/pages/admin/config/admin-menus'
import './admin-pc.less'

const ADMIN_PC_CLASS = 'admin-pc-mode'

export default {
  name: 'H5AdminLayout',
  data() {
    return {
      userInfo: getH5AdminUserCache(),
      schoolsSession: null,
      activeOrgId: getSchoolsActiveOrgId(),
      routeLoading: false,
      routeLoadingTimer: null
    }
  },
  computed: {
    activeMenu() {
      const path = this.$route.path
      if (path.indexOf('/admin/schools') === 0) {
        return resolveSchoolsAdminActiveMenu(path)
      }
      return resolveH5AdminActiveMenu(path)
    },
    displayName() {
      if (!this.userInfo) return '管理员'
      return this.userInfo.nickName || this.userInfo.username || '管理员'
    },
    isPlatformAdmin() {
      return !!(this.userInfo && this.userInfo.schoolsPlatformAdmin)
    },
    visibleH5Menus() {
      return filterH5AdminMenus(this.userInfo)
    },
    visibleSchoolsMenus() {
      return filterSchoolsAdminMenus(this.userInfo)
    },
    /** 权限异步回来后强制重挂载 el-menu，避免 submenu 不刷新 / default-openeds 不生效 */
    sideMenuKey() {
      const schools = this.visibleSchoolsMenus.map((i) => i.key).join(',')
      const top = this.visibleH5Menus.map((i) => i.key).join(',')
      return [top, schools].join('|')
    },
    defaultOpeneds() {
      const open = []
      if (this.visibleSchoolsMenus.length) open.push('schools')
      return open
    },
    orgOptions() {
      return (this.schoolsSession && this.schoolsSession.orgOptions) || []
    },
    showSchoolsOrgBar() {
      // 未开通任何教培菜单时不展示机构条，避免侧栏出现「演示教培机构」等无关信息
      if (!this.visibleSchoolsMenus.length) return false
      return this.isPlatformAdmin || !!(this.schoolsSession && this.schoolsSession.orgId)
    },
    currentOrgLabel() {
      if (this.schoolsSession && this.schoolsSession.orgName) {
        return this.schoolsSession.orgName
      }
      if (this.userInfo && this.userInfo.schoolsOrgName) {
        return this.userInfo.schoolsOrgName
      }
      return '未绑定机构'
    },
    schoolsOrgMissingTip() {
      const path = this.$route.path || ''
      if (path.indexOf('/admin/schools') !== 0) return ''
      if (path.indexOf('/admin/schools/platform-orgs') === 0) return ''
      if (this.isPlatformAdmin && !getSchoolsActiveOrgId()) {
        return '平台超管请先选择机构，或在「机构管理」中开户后进入对应机构'
      }
      if (!this.isPlatformAdmin && !(this.schoolsSession && this.schoolsSession.orgId)) {
        return '当前账号未绑定教培机构，请联系平台管理员开户'
      }
      return ''
    }
  },
  created() {
    document.documentElement.classList.add(ADMIN_PC_CLASS)
    if (!getH5AdminToken()) {
      this.$router.replace({ path: '/admin/login', query: { redirect: this.$route.fullPath } })
      return
    }
    this.loadUserInfo()
  },
  watch: {
    '$route.path'(path, prevPath) {
      if (path !== prevPath) {
        this.triggerRouteLoading()
      }
    }
  },
  beforeDestroy() {
    document.documentElement.classList.remove(ADMIN_PC_CLASS)
    clearTimeout(this.routeLoadingTimer)
  },
  methods: {
    loadUserInfo() {
      h5AdminGetInfo()
        .then(res => {
          // ApiResult: { data: user }；兼容偶发直接返回 user
          const user = (res && res.data && typeof res.data === 'object' && !Array.isArray(res.data))
            ? res.data
            : (res && res.username ? res : null)
          if (user) {
            // 强制替换，确保侧栏菜单按最新 h5AdminMenus 重算
            this.userInfo = Object.assign({}, user)
            try {
              window.sessionStorage.setItem('h5_admin_user', JSON.stringify(user))
            } catch (e) {
              // ignore
            }
          }
          return this.loadSchoolsOrgSession()
        })
        .catch(() => {})
    },
    loadSchoolsOrgSession() {
      return fetchSchoolsOrgSession()
        .then(session => {
          this.schoolsSession = session || null
          if (session && session.orgId && !this.isPlatformAdmin) {
            setSchoolsActiveOrgId(session.orgId)
            this.activeOrgId = session.orgId
          } else if (this.isPlatformAdmin) {
            const saved = getSchoolsActiveOrgId()
            this.activeOrgId = saved || null
          }
        })
        .catch(() => {})
    },
    handleOrgSwitch(orgId) {
      setSchoolsActiveOrgId(orgId || null)
      this.activeOrgId = orgId || null
      this.loadSchoolsOrgSession().then(() => {
        if (orgId && this.$route.path.indexOf('/admin/schools') === 0
          && this.$route.path.indexOf('/admin/schools/platform-orgs') !== 0) {
          this.$router.replace('/admin/schools/dashboard')
        }
      })
    },
    handleCommand(command) {
      if (command === 'logout') {
        this.handleLogout()
      }
    },
    handleLogout() {
      this.$confirm('确定退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        h5AdminLogout().finally(() => {
          removeH5AdminSession()
          this.$router.replace('/admin/login')
        })
      }).catch(() => {})
    },
    triggerRouteLoading() {
      this.routeLoading = true
      clearTimeout(this.routeLoadingTimer)
      this.routeLoadingTimer = setTimeout(() => {
        this.routeLoading = false
      }, 320)
    }
  }
}
</script>
